import shortid from 'shortid'
import React from 'react';
import store from '../../store.js';
import deepEqual from 'deep-equal'
import deepCopy from 'deep-copy'
import * as WizardClasses from './WizardClasses'
import { getCountryOptions } from './selectOptions'
import { getUploadsFromFields } from './fieldComponents/UploadAccumulator'
import { UCCSearchDisplay } from './fieldComponents/UCCSearch'
import ReactMarkdown from 'components/Markdown/Markdown'
import { FormattedAddress, coerceType } from 'utilities'
import { BUSINESS_RULES } from './businessRules'
import { resolveFieldConditions } from '@tecuity/wizard-utilities'
import format from 'date-fns/format'
import isValid from 'date-fns/isValid'
import parseISO from 'date-fns/parseISO'
import { BASE_URL } from '../../requests.js';

export class Step {
  constructor(props = {}){
    this.classType = "Step"
    this.stepId = props.stepId || shortid.generate();
    this.templateId = props.templateId || "";
    this.title = props.title || "New Step";
    this.active = props.active || false;
    this.includeInSummary = (props.includeInSummary === false ? false : true);
    this.isValid = props.valid || null;
    this.help = props.help || "";
    this.sectionOrder = props.sectionOrder || [];
    this.hideStepOnForm = props.hideStepOnForm || false;
  }
}

export class Section {
  constructor(section = {}){
    this.sectionId = section.sectionId || shortid.generate();
    this.title = section.title === undefined ? "New Section" : section.title;
    this.description = section.description || "";
    this.type = section.type || "custom";
    this.fields = section.fields || [];
    this.stepId = section.stepId || null;
    this.fieldOrder = section.fieldOrder || [];
    this.hideSectionOnForm = section.hideSectionOnForm || false;
    this.hideTitleOnForm = section.hideTitleOnForm || false;
    this.showSubHeader = section.showSubHeader || false;
    this.subHeaderLabel = section.subHeaderLabel || "";
    this.allowPageBreak = section.allowPageBreak || false;
  }
}

export class Confirm extends Section {
  constructor(section = {}){
    super(section);
    this.type = section.type || "confirm";
  }
}

export class Field {
  constructor(field = {}){
    this.fieldId = field.fieldId || shortid.generate();
    this.name = field.name || "FIELD_NAME_" + shortid.generate().toUpperCase()
    this.label = field.label ? field.label : field.label === '' ? '' : 'New Field';
    this.shortLabel = field.shortLabel ? field.shortLabel : '';
    this.description = field.description || "";
    this.tooltip = field.tooltip || "";
    this.type = field.type || "text";
    this.fieldClass = "Field";
    this.width = field.width || "60%";
    this.includeInSummary = field.includeInSummary === false ? false : true;
    this.placeholder = field.placeholder || "";
    this.value = typeof field.value === "number" ? field.value.toString() : field.value || "";
    this.validation = field.validation || {onChange: [], onBlur: [], onAdvance: []};
    this.errorMessage = field.errorMessage || "";
    this.infoMessage = field.infoMessage || "";
    this.allowInfoMessage = field.allowInfoMessage === false ? false : true;
    this.mask = field.mask || [];
    this.isValid = field.isValid || null;
    this.required = field.required || false;
    this.disabled = field.disabled || false;
    this.disabledDuringCorrection =  field.disabledDuringCorrection || false;
    this.disabledDuringEdit =  field.disabledDuringEdit || false;
    this.active = field.active === false ? false : true;
    this.selectedIndex = field.selectedIndex || 0;
    this.inheritValueFrom = field.inheritValueFrom || null;
    this.ownsLabel = (field.ownsLabel === true ? true : false);
    this.hideLabel = (field.hideLabel === true ? true : false);
    this.options = field.options || null;
    if(this.type === "number"){
      this.mask = field.mask || [{fn: "onlyNumbers"}]
      this.allowedRules = ["notEmptyOrNull", "numberGreaterThan", "numberLessThan"]
    }
    this.businessRules = field.businessRules || [];
    this.allowedRules = [
      'notEmptyOrNull',
      'validFeinNum',
      'validPhoneNumber',
      'validPhoneNumberIfPresent',
      'numberGreaterThan',
      'numberLessThan',
      'validZipCode',
      'validSocial',
      'isValidEmailFormat',
      'onlyNumbers',
      'maxCharacters',
      'minCharacters',
      'checkForRegulatedWords',
      'customBusinessRules'
    ]
    this.allowedMasks = ['phone', 'onlyNumbers', 'fein', 'currency', 'maxStringLength', 'zipCode', 'social'];
    this.allowedRepeaterConstraints = this.type === 'text' ? ['mustBeLessThanOrEqualToColumn', 'xMayNotEqualY'] : []
    this.parent = field.parent || null;
    this.defaultValue = field.defaultValue || '';
    this.textAlignment = field.textAlignment || 'left'
    this.alwaysIncludeInSummary = field.alwaysIncludeInSummary || false;
    this.passwordMask = field.passwordMask || false;
    this.hideFieldOnForm = field.hideFieldOnForm || false;
    this.hideLabelOnForm = field.hideLabelOnForm || false;
    this.formDisplayType = field.formDisplayType || 'default';
    this.dynamic = field.dynamic || false;
    this.excludeFromDiff = field.excludeFromDiff || false;
    this.internalReviewRequiredIfChanged = field.internalReviewRequiredIfChanged || false;
    this.conditions = field.conditions ? field.conditions.length > 0 ? field.conditions : null : null;
    this.conditionsOperator = field.conditionsOperator || 'and';
    this.logicNodes = field.logicNodes || {};
    this.logicComments = field.logicComments || {};
    this.redact = field.redact || false;
    this.encrypt = field.encrypt || false;
    this.prepopulationDisabled = field.prepopulationDisabled || false;
    this.allowCorrectionPopulation = field.allowCorrectionPopulation || false;
    this.repeaterConstraints = field.repeaterConstraints || []
    this.isStaticField = field.isStaticField || false;
    this.frColumnWidth = field.frColumnWidth || 20;
    this.frEnableWidth = field.frEnableWidth || false;
    this.hideInRepeaterGrid = field.hideInRepeaterGrid || false;
    this.breakFieldInside = field.breakFieldInside || false;
    this.getGroupItemsOnLoad = field.getGroupItemsOnLoad || false;
  }
  getConfirmFieldProps(){
    return [{label: this.label, value: this.value}]
  }
  getKeyValuePairs(field){
    return[{name: field.name, value: typeof field.value === 'string' ? field.type === "paragraph" ? field.value.replace(/\s*$/,"") : field.value.trim() : field.value}]
  }
  getCompareValue(field={}){
    return field.value
  }
  getHumanReadableValue(field, value){
    return value;
  }
  static compareValues(originalValue, currentValue){
    return !deepEqual(originalValue, currentValue)
  }

}

export class TextArea extends Field{
  constructor(props = {}){
    super(props);
    this.type = "textArea";
    this.fieldClass = "TextArea";
    this.rows = props.rows || 5;
  }
}

export class Checkbox extends Field{
  constructor(props = {}){
    super(props);
    this.value = props.value === 'Y' || props.value === true ? true : false;
    this.type = "checkbox";
    this.fieldClass = "Checkbox";
    this.label = props.label || "New Checkbox";
    this.ownsLabel = true;
    this.hideLabel = true;
    this.alwaysShowDescription = true;
    this.allowedRules = ["mustBeTrue", 'customBusinessRules'];
    this.allowedRepeaterConstraints = ["minNumberChecked", "minNumberUnchecked"];
  }
  getConfirmFieldProps(){
    return [{label: this.label, value: (this.value === true ? "Yes" : "No")}]
  }
  getHumanReadableValue(field, value){
    return value ? "Yes" : "No";
  }

}

export class DateInput extends Field {
  constructor(props = {}) {
    super(props)
    this.value = props.value || null
    this.type = props.type || 'date'
    this.fieldClass = 'DateInput'
    this.width = props.width || '30%'
    this.allowedRules = [
      'notEmptyOrNull',
      'enforceMaxDaysBeforeToday',
      'enforceMaxDaysAfterToday',
      'enforceMinDaysBeforeToday',
      'enforceMinDaysAfterToday',
      'enforceMaxMonthsBeforeToday',
      'enforceMaxMonthsAfterToday',
      'enforceMinMonthsBeforeToday',
      'enforceMinMonthsAfterToday',
      'minDate',
      'maxDate',
      'customBusinessRules'
    ]
    this.todayButton = props.todayButton === false ? false : true
  }
  getDateString() {
    return format(this.value, 'MM/dd/yyyy HH:mm:ss.sss')
  }
  getConfirmFieldProps() {
    return [{ label: this.label, value: format(this.value, 'MM/dd/yyyy') }]
  }
  getHumanReadableValue(field, value) {
    return value ? format(new Date(value), 'MM/dd/yyyy') : value
  }
  getKeyValuePairs(field) {
    return [
      {
        name: field.name,
        value: field.value ? format(new Date(field.value), 'MM/dd/yyyy') : field.value
      }
    ]
  }

}

export class DateRange extends DateInput{
  constructor(props = {}){
    super(props)
    this.type = 'DateRange';
    this.fieldClass = 'DateRange';
    this.value = props.value ? {start: props.value.start, end: props.value.end} : {start: null, end: null}
    this.allowedRules = ['dateRangeRequired', 'minDateRange', 'maxDateRange', 'customBusinessRules'];
    this.label = props.label || 'Date Range';
    this.startLabel = props.startLabel || 'Start date';
    this.endLabel = props.endLabel || 'End date';
    this.hideLabel = true;
    this.ownsLabel = true;
    this.todayButton = props.todayButton === false ? false : true;
  }
  getHumanReadableValue(field, value){
    return `${value.start ? format(new Date(value.start), "M/d/yyyy") : "None"} - ${value.end ? format(new Date(value.end), "M/d/yyyy") : "None"}`
  }
  getKeyValuePairs(field){
    return [{name: field.name, value: {
      start: field.value.start ? format(new Date(field.value.start), 'MM/dd/yyyy') : field.value.end,
      end: field.value.end ? format(new Date(field.value.end), 'MM/dd/yyyy') : field.value.end
    }}]
  }
}

export class TimeInput extends Field {
  constructor(props = {}) {
    super(props)
    this.type = 'TimeInput'
    this.fieldClass = 'TimeInput'
    this.value =
      props.value && props.value.length >= 7
        ? props.value
        : `${props.defaultTime || ''} ${props.defaultPeriod || 'am'}`
    this.label = props.label || 'Time'
    this.allowedRules = ['timeRequired', 'customBusinessRules']
    this.allowedMasks = []
    this.mask = []
    this.defaultTime = props.defaultTime || null
    this.defaultPeriod = props.defaultPeriod || null
  }
}

export class Radio extends Field{
  constructor(radio = {}){
    super(radio);
    this.type = "radio";
    this.fieldClass = "Radio";
    this.options = radio.options || [{value: "option1", label: "Option 1"}];
    this.value = (this.options.find((option)=>(option.value === this.value)) && radio.value !== undefined ? radio.value : "default");
    this.allowedRules = ["mustBeSelected", 'customBusinessRules'];
    this.allowedRepeaterConstraints = ["maxNumberOfSelectedOption", "minNumberOfSelectedOption", "xMayNotEqualY"];
  }
  getConfirmFieldProps(){
    return [{label: this.label, value: this.options[(this.selectedIndex ? this.selectedIndex : 0)].label}]
  }
  getHumanReadableValue(field, value){
    let selectedOption = field.options.find((option)=>(option.value === value));
    return selectedOption ? selectedOption.label : "";
  }
  getKeyValuePairs(field){
    let value = field.value;
    if(value === 'default' || !field.options.find(opt => opt.value === value)){
      value = ''
    }
    return [{name: field.name, value}]
  }

}

export class Select extends Radio{
  constructor(props = {}){
    super(props);
    this.type = "select";
    this.fieldClass = "Select";
    this.value = props.value || (this.options.find((option)=>(option.value === this.value)) && props.value ? props.value : "default");
    this.options = props.options || [{value: "default", label: "[Select An Option]"},{value: "option1", label: "Option 1"}];
    this.allowedRules = ["mustBeSelected", 'customBusinessRules'];
    this.optionGroups = props.optionGroups || null;
  }
  getConfirmFieldProps(){
    return [{label: this.label, value: this.options[(this.selectedIndex ? this.selectedIndex : 0)].label}]
  }
  getHumanReadableValue(field, value){
    let selectedOption = field.options.find((option)=>(option.value === value));
    return selectedOption ? selectedOption.label : "";
  }
}

export class FileUpload extends Field {
  constructor(props = {}) {
    super(props)
    this.type = 'file'
    this.fieldClass = 'FileUpload'
    this.value = props.value || []
    this.metadata = props.metadata || {}
    this.accept = props.accept !== undefined ? props.accept : '.pdf'
    this.requireInternalReview = props.requireInternalReview || false
    this.sortOrder = props.sortOrder || 0
    this.imageTypeId = props.imageTypeId || ''
    this.multiple = props.multiple || false
    this.allowedRules = ['uploadImmediatelyRequired', 'customBusinessRules']
    this.businessRules = this.resolveBusinessRules(props.businessRules || [])
  }
  resolveBusinessRules = (rules) =>
    rules.map((rule) => ({
      ...rule,
      fn: rule.fn === 'uploadRequired' ? 'uploadImmediatelyRequired' : rule.fn,
    }))
  getHumanReadableValue(field, value) {
    return (
      <span>
        {value
          .filter((x) => x.status === 'success')
          .map((file, i) => (
            <React.Fragment key={i}>
              <span key={file.queueId}>
                {process.env.WIZARD_UPLOADS_DOWNLOADABLE ? (
                  <a download href={`${BASE_URL}/api/FileUpload/${file.guid}`}>
                    {file.filename}
                  </a>
                ) : (
                  file.filename
                )}
              </span>
              {value.length - 1 !== i && <br />}
            </React.Fragment>
          ))}
      </span>
    )
  }
  getConfirmFieldProps() {
    return [{ label: this.label, value: this.value.filename }]
  }
  getKeyValuePairs(field) {
    return [
      {
        name: field.name,
        value:
          field.value && Array.isArray(field.value)
            ? field.value.filter((x) => x.status === 'success')
            : field.value,
      },
    ]
  }

}

export class UploadPlaceholder extends FileUpload{
  constructor(props = {}){
    super(props)
    this.type = "UploadPlaceholder";
    this.fieldClass = "UploadPlaceholder";
    this.value = props.value || null;
    this.requireInternalReview = props.requireInternalReview || false;
    this.allowedRules = ['uploadRequired', 'customBusinessRules'];
    this.includeInSummary = false;
    this.businessRules = props.businessRules || []
  }
  getHumanReadableValue(field, value){
    return field.description;
  }
}

export class UploadAccumulator extends Field{
  constructor(props = {}){
    super(props)
    this.type = "UploadAccumulator";
    this.fieldClass = "UploadAccumulator";
    this.value = props.value || {};
    this.businessRules = [{fn: 'enforceUploadPlaceholderRules', ruleId: 'enforceUploadPlaceholderRules', props: {}}]
    this.delegateErrorsToChildren = true;
    this.instructions = props.instructions || '';
  }
  getHumanReadableValue(field, value){
    return (
      <span>
        {
          Object.keys(value).reduce((array, currentFieldId) => ([
            ...array,
            ...value[currentFieldId]
          ]), [])
          .map(file => (
            <span className='file' key={file.queueId}>{file.filename}</span>
          ))
        }
      </span>
    )
  }
}

export class ConfirmFields extends Field {
  constructor(props = {}) {
    super(props)
    this.confirmFieldId = props.confirmFieldId || shortid.generate()
    this.type = 'confirmFields'
    this.fieldClass = 'ConfirmFields'
    this.match = props.match || null
    this.value = props.value || { val1: '', val2: '' }
    this.label = props.label || 'New Confirm Field'
    this.ownsLabel = true
    this.hideLabel = true
    this.allowedRules = [
      'notEmptyOrNull',
      'valuesMatch',
      'noRegulatedWords',
      'hasNameEnding',
      'nameNotTaken',
      'isValidName',
      'isValidEmailFormat',
      'checkForRegulatedWords',
      'customBusinessRules',
    ]
    this.allowedMasks = ['onlyAscii']
    this.confirmFieldLabel = props.confirmFieldLabel || ''
    this.maskTopField = props.maskTopField === false ? false : true
  }
  getKeyValuePairs(field) {
    return [{ name: field.name, value: field.value.val1.trim() }]
  }
  getConfirmFieldProps() {
    return [{ label: this.fields[0].label, value: this.value.val1 }]
  }
  getHumanReadableValue(field, value) {
    return value.val1
  }

}

const getEntityNameSearchValueObject = ({reservedYN, ...rest}) => ({
  val1: '',
  val2: '',
  reservedYN: reservedYN || '',
  additionalYN: '',
  consentYN: '',
  reservedNameId: '',
  additionalNameId: '',
  numConsents: '',
  reservedNum: '',
  reservedName: '',
  additionalMode: 'trade',
  ...rest
})

export class EntityNameSearch extends Field {
  constructor(props = {}) {
    super(props)
    this.type = 'EntityNameSearch'
    this.fieldClass = 'EntityNameSearch'
    this.label = props.label ? props.label : props.label === '' ? '' : 'Entity name'
    this.hideLabel = true
    this.value =
      {
        ...getEntityNameSearchValueObject({
          ...props.value,
          reservedYN: (props.value || {}).reservedYN || props.defaultReservedValue
        })
      } || getEntityNameSearchValueObject({ reservedYN: props.defaultReservedValue })
    this.allowedRules = ['validateEntityNameFields', 'checkForRegulatedWords', 'customBusinessRules', 'nameNotTaken']
    this.allowedMasks = ['onlyAsciiConfirmFields']
    this.mask = props.mask || [{ fn: 'onlyAsciiConfirmFields', props: {} }]
    this.delegateErrorsToChildren = true
    this.metadata = props.metadata || {}
    this.requireSupportingDocument = props.requireSupportingDocument === 'Y' ? 'Y' : 'N'
    //Reserved name props
    this.hideReserved = props.hideReserved || false
    this.reservedLabel =
      props.reservedLabel || 'A previously filed reserved name will be used for this filing.'
    this.allowReservedNameEditing = props.allowReservedNameEditing || false
    this.defaultReservedValue =
      props.defaultReservedValue !== undefined ? props.defaultReservedValue : undefined
    //Entity name props
    this.nameLabel = props.nameLabel || 'Business name'
    this.nameDescription = props.nameDescription || ''
    this.nameConfirmLabel = props.nameConfirmLabel || 'Confirm business name'
    this.nameWidth = props.nameWidth || '40%'
    this.nameMatch = props.match || null
    //Additional name props
    this.additionalName = props.additionalName || ''
    this.hideAdditionalName = props.hideAdditionalName || false
    this.additionalLabel = props.additionalLabel || 'Trade name'
    this.additionalDescription = props.additionalDescription || ''
    //Consent props
    this.hideConsent = props.hideConsent || false
    this.consentLabel = props.consentLabel || 'Consent to use name'
    this.consentDescription = props.consentDescription || ''
    this.tradeOptionLabel1 = props.tradeOptionLabel1 || 'It is not known if a trade name is needed.'
    this.tradeOptionLabel2 =
      props.tradeOptionLabel2 ||
      'A trade name is needed because the name is not in the required form or is deceptively similar to another name and a Trade Name Registration has been filed.'
    this.consentOptionLabel1 =
      props.consentOptionLabel1 ||
      'It is not known if consent to use the name is needed from a business registered in [State].'
    this.consentOptionLabel2 =
      props.consentOptionLabel2 ||
      'Consent to use the name was obtained because the business name is in conflict with a name registered in [State].'
    this.consentInstructions = props.consentInstructions || ''
    this.hideNumConsent = props.hideNumConsent || false
    this.selectedAdditionalNameCache = props.selectedAdditionalNameCache || '';
  }
  getHumanReadableValue(field, value) {
    if (value.reservedYN === 'N' || field.hideReserved) {
      if (value.additionalYN === 'Y') {
        return (
          <span>
            {value.val1}
            <br />
            <span className="indented">
              transacting business in {process.env.STATE_NAME} under the name of
            </span>
            <br />
            {value.additionalName}
          </span>
        )
      } else {
        return value.val1
      }
    } else {
      return `${value.reservedNum} - ${value.reservedName}`
    }
  }
  getKeyValuePairs(field) {
    return [
      {
        name: field.name,
        value: { ...field.value, val1: field.value.val1.trim(), val2: field.value.val2.trim() }
      }
    ]
  }
}

export const getAddressObject = (value = {}) => {
  return {
    ADDR1: coerceType(value.ADDR1 || '', ''),
    ADDR2: coerceType(value.ADDR2 || '', ''),
    ADDR3: coerceType(value.ADDR3 || '', ''),
    CITY: coerceType(value.CITY || '', ''),
    STATE: coerceType(value.STATE || '', ''),
    COUNTY: coerceType(value.COUNTY || '', ''),
    COUNTRY: coerceType(process.env.ALLOW_EMPTY_COUNTRY ? value.COUNTRY : value.COUNTRY || 'United States', 'United States'),
    POSTAL_CODE: coerceType(value.POSTAL_CODE || '', '')
  }
}

export class AddressBlock extends Field {
  constructor(block = {}) {
    super(block)
    this.type = 'addressBlock'
    this.fieldClass = 'AddressBlock'
    this.label = block.label || 'Address'
    this.value = block.value ? getAddressObject(block.value) : getAddressObject()
    if (this.value.COUNTRY === '' && !process.env.ALLOW_EMPTY_COUNTRY) this.value.COUNTRY = 'United States'
    this.showCityStateZip = block.showCityStateZip === false ? false : true
    this.showCounty = block.showCounty === true ? true : false
    this.showCountry = block.showCountry === true ? true : false
    this.disableState = block.disableState === true ? true : false
    this.disableCountry = block.disableCountry === true ? true : false
    this.hideAttention = block.hideAttention === true ? true : false
    this.feedback = block.feedback || null
    this.bubbleEvents = true
    this.hideLabel = true
    this.defaultState = block.defaultState || ''
    this.allowedRules = [
      'addressIsRequired',
      'addressIsValid',
      'partialAddressNotAllowed',
      'poBoxNotAllowed',
      'checkForRegulatedWords',
      'customBusinessRules',
    ]
    this.allowedMasks = ['addressUpperCase']
    this.allowedRepeaterConstraints = ['atLeastSomeStateMustBe']
    this.autoPopulateFromAMS = block.autoPopulateFromAMS || false
    this.excludeCurrentState = block.excludeCurrentState || false
    this.bypassMessage = block.bypassMessage || null
    this.primarybypassMessage = block.primarybypassMessage || null
    this.mailbypassMessage = block.mailbypassMessage || null
  }
  formatAddress(value, field) {
    let country = getCountryOptions().find((option) => option.value === value.COUNTRY)
    const DISPLAY_FULL_FOREIGN_ADDR = process.env.DISPLAY_FULL_FOREIGN_ADDR
    return (
      <span>
        {DISPLAY_FULL_FOREIGN_ADDR && field.showCountry && value.COUNTRY !== '' && value.COUNTRY !== 'United States'
          ? country
            ? country.label
            : null
          : null}
        {DISPLAY_FULL_FOREIGN_ADDR && field.showCountry && value.COUNTRY !== '' && value.COUNTRY !== 'United States' ? <br /> : null}
        {value.ADDR1} <br />
        {value.ADDR2}
        {value.ADDR2 ? <br /> : null}
        {value.ADDR3}
        {value.ADDR3 ? <br /> : null}
        {(DISPLAY_FULL_FOREIGN_ADDR || value.COUNTRY === 'United States') && value.ADDR1 !== ''
          ? value.CITY + ', ' + value.STATE + ' ' + value.POSTAL_CODE
          : null}
        {value.COUNTRY === '' ? <br /> : null}
        {value.COUNTY !== '' && value.COUNTRY !== '' ? <br /> : null}
        {value.COUNTY !== '' && field.showCounty ? value.COUNTY + ', ' : null}
        {!DISPLAY_FULL_FOREIGN_ADDR && field.showCountry && value.COUNTRY !== '' && value.COUNTRY !== 'United States'
          ? country
            ? country.label
            : null
          : null}
      </span>
    )
  }
  getConfirmFieldProps() {
    return [{ label: this.label, value: this.formatAddress(this.value) }]
  }
  getHumanReadableValue(field, value) {
    return this.formatAddress(value, field)
  }
  getKeyValuePairs(field) {
    let value = { ...field.value }
    const isForeign =
      (value.COUNTRY === null ? '' : value.COUNTRY).toLowerCase() !== 'united states'
    const showCityStateZipOnForeignAddress = process.env.USE_CITY_STATE_ON_FOREIGN_ADDRESS; 
    if (isForeign) {
      value = {
        ...value,
        STATE: showCityStateZipOnForeignAddress ? (value.STATE || '').trim() : '',
        CITY: showCityStateZipOnForeignAddress ? (value.CITY || '').trim() : '',
        POSTAL_CODE: showCityStateZipOnForeignAddress ? (value.POSTAL_CODE || '').trim() : '',
      }
    } else {
      value = {
        ...value,
        ADDR1: (value.ADDR1 || '').trim(),
        ADDR2: (value.ADDR2 || '').trim(),
        ADDR3: (value.ADDR3 || '').trim(),
        CITY: (value.CITY || '').trim(),
        STATE: value.STATE === '' ? field.defaultState : value.STATE,
      }
    }
    return [{ name: field.name, value }]
  }

}

export class FullName extends Field {
  constructor(props = {}){
    super(props)
    this.type = "FullName";
    this.fieldClass = "FullName";
    this.label = props.label || "Full Name";
    this.hideLabel = true;
    this.value = props.value || {FIRST_NAME: "", MIDDLE_NAME: "", LAST_NAME: "", SUFFIX: ""}
    this.middleInitial = props.middleInitial || false;
    this.suffix = props.suffix || false;
    this.allowedRules = ["nameRequired", 'checkForRegulatedWords', 'customBusinessRules']
    this.onlyRequireLastName = props.onlyRequireLastName || false
  }
  getConfirmFieldProps(){
    return [
      {label: "First Name", value: this.value.FIRST_NAME},
      {label: "Middle Name", value: this.value.MIDDLE_NAME},
      {label: "Last Name", value: this.value.LAST_NAME}
    ]
  }
  getHumanReadableValue(field, value){
    return value.FIRST_NAME + (value.MIDDLE_NAME !== "" ? " " + value.MIDDLE_NAME + " " : " ") + value.LAST_NAME + (value.SUFFIX ? ` ${value.SUFFIX}` : '')
  }

}

const getNameOrgObject = (value = {}, defaultIsOrg)=>({
  ORG_YN: value.ORG_YN !== undefined ? value.ORG_YN : defaultIsOrg,
  FIRST_NAME: value.FIRST_NAME || "",
  MIDDLE_NAME: value.MIDDLE_NAME || "",
  LAST_NAME: value.LAST_NAME || "",
  ORG_NAME: value.ORG_NAME || "",
  SUFFIX: value.SUFFIX || ""
})

export class NameOrg extends FullName {
  constructor(props = {}){
    super(props)
    this.type = "NameOrg"
    this.fieldClass = "NameOrg"
    this.label = props.label || "Name of individual or organization"
    this.hideLabel = true;
    this.defaultIsOrg = props.defaultIsOrg || false;
    this.value = props.value ? getNameOrgObject(props.value) : getNameOrgObject({}, this.defaultIsOrg)
    this.allowedRules = ["nameOrgRequired", 'checkForRegulatedWords', 'customBusinessRules']
    this.checkboxLabel = props.checkboxLabel || ""
    this.onlyRequireLastName = props.onlyRequireLastName || false
    this.ownsTooltip = true;
  }
  getConfirmFieldProps(){
    if(this.value.ORG_YN){
      return [
        {label: "Organization", value: this.value.ORG_NAME}
      ]
    }else{
      return [
        {label: "First Name", value: this.value.FIRST_NAME},
        {label: "Middle Name", value: this.value.MIDDLE_NAME},
        {label: "Last Name", value: this.value.LAST_NAME}
      ]
    }
  }
  getCompareValue(field={}){
    const { value = {} } = field
    const isOrganization = value.ORG_YN === 'N' || value.ORG_YN === false ? false : true
    return isOrganization
      ? value.ORG_NAME :
      {
        FIRST_NAME: value.FIRST_NAME,
        LAST_NAME: value.LAST_NAME,
        MIDDLE_NAME: value.MIDDLE_NAME,
        SUFFIX: value.SUFFIX
      }
  }
  getHumanReadableValue(field, value){
    const isOrganization = value.ORG_YN === 'N' || value.ORG_YN === false ? false : true
    if (isOrganization) {
      return value.ORG_NAME
    } else {
      return `${value.FIRST_NAME} ${value.MIDDLE_NAME !== '' ? value.MIDDLE_NAME + ' ' : ''}${value.LAST_NAME}${
        value.SUFFIX ? ` ${value.SUFFIX}` : ''
      }`
    }
  }
  getKeyValuePairs(field){
    const ORG_YN = field.value.ORG_YN === false || field.value.ORG_YN === 'N' ? 'N' : 'Y';
    return [
    	{
    		name: field.name,
    		value: {
          ...field.value,
          ORG_YN,
          ORG_NAME: ORG_YN === 'Y' ? field.value.ORG_NAME : '',
          FIRST_NAME: ORG_YN === 'N' ? field.value.FIRST_NAME : '',
          MIDDLE_NAME: ORG_YN === 'N' ? field.value.MIDDLE_NAME : '',
          LAST_NAME: ORG_YN === 'N' ? field.value.LAST_NAME : '',
          SUFFIX: ORG_YN === 'N' ? field.value.SUFFIX : ''
        }
    	}
    ]
  }

}

const getNameQualifiedOrgObject = ()=>({FIRST_NAME: "", MIDDLE_NAME: "", LAST_NAME: "", SUFFIX: "", ORG_NAME: "", ORG_TYPE: "", SELECTED: null, SOURCE_ID : 0, SEARCH_TYPE: "", SOURCE_TYPE_ID : 54})

export class NameQualifiedOrg extends NameOrg {
  constructor(props = {}){
    super(props)
    this.type = "NameQualifiedOrg"
    this.fieldClass = "NameQualifiedOrg"
    this.label = props.label || "Name of individual or organization"
    this.hideLabel = false;
    this.value = props.value ? {...getNameQualifiedOrgObject(), ...props.value} : getNameQualifiedOrgObject()
    this.allowedRules = ["nameQualifiedOrgRequired", 'checkForRegulatedWords', 'customBusinessRules']
    this.suffix = props.suffix || false;
    this.checkboxLabel = props.checkboxLabel || ""
    this.individualLabel = props.individualLabel || props.individualLabel === "" ? props.individualLabel : "Individual";
    this.qualifiedLabel = props.qualifiedLabel || props.qualifiedLabel === "" ? props.qualifiedLabel : "Qualified Organization";
    this.unqualifiedLabel = props.unqualifiedLabel || props.unqualifiedLabel === "" ? props.unqualifiedLabel : "Unqualified Organization";
    this.qualifiedPlaceholder = props.qualifiedPlaceholder || "(Search by name of organization)";
    this.unqualifiedPlaceholder = props.unqualifiedPlaceholder || "";
    this.modalTitle = props.modalTitle || "";
    this.metadata = props.metadata || {};
    this.value = {...this.value, ORG_TYPE: this.getValueOrDefaultOrgType(this)} || getNameQualifiedOrgObject()
    this.searchType = props.searchType || '';
    this.additionalParameters = props.additionalParameters || {};
    this.parameterFields = props.parameterFields || [];
  }
  getCompareValue(field={}){
    const { value } = field
    switch (value.ORG_TYPE) {
      case '0':
      case 0:
        return {
          ORG_TYPE: value.ORG_TYPE,
          FIRST_NAME: value.FIRST_NAME,
          LAST_NAME: value.LAST_NAME,
          MIDDLE_NAME: value.MIDDLE_NAME,
          SUFFIX: value.SUFFIX
        }
      case '1':
      case 1:
        return {
          ORG_TYPE: value.ORG_TYPE,
          SELECTED: value.SELECTED,
          SOURCE_ID: value.SOURCE_ID,
          ORG_NAME: value.ORG_NAME
        }
      case '2':
      case 2:
        return {
          ORG_TYPE: value.ORG_TYPE,
          ORG_NAME: value.ORG_NAME,
          SELECTED: value.SELECTED,
          SOURCE_ID: value.SOURCE_ID
        }
      default:
        return value;
    }
  }
  getValueOrDefaultOrgType(props){
    const orgType = props.value.ORG_TYPE.toString();
    const labelArray = [props.individualLabel, props.qualifiedLabel, props.unqualifiedLabel];
    const selectedValueExists = labelArray.reduce((exists, label, index)=>{
     if(orgType === index.toString() && label !== ""){
       return true;
     }else{
       return exists;
     }
    }, false);
    if(selectedValueExists){
      return orgType;
    }else{
      return "";
    }
  }
  getHumanReadableValue(field, value){
    switch (value.ORG_TYPE){
      case "0":
        return value.FIRST_NAME + (value.MIDDLE_NAME !== "" ? " " + value.MIDDLE_NAME + " " : " ") + value.LAST_NAME + (value.SUFFIX ? ` ${value.SUFFIX}` : '')
      case "1":
      if(value.SELECTED){
        return <HumanReadableResultCard value={value.SELECTED} />
      }
      break;
      case "2":
        return value.ORG_NAME
    }
  }
  getHumanReadableSignatureValue(field, value){
    switch (value.ORG_TYPE){
      case "0":
        return value.FIRST_NAME + (value.MIDDLE_NAME !== "" ? " " + value.MIDDLE_NAME + " " : " ") + value.LAST_NAME + (value.SUFFIX ? ` ${value.SUFFIX}` : '')
      case "1":
      if(value.SELECTED){
        return value.SELECTED.HEADER1
      }
      break;
      case "2":
        return value.ORG_NAME
    }
  }
  getKeyValuePairs(field){
    return [{name: field.name,
      value: {...field.value,
        SOURCE_ID: (field.value.SELECTED && field.value.SELECTED.SOURCE_ID) || 0,
        ORG_YN : field.value.ORG_TYPE === "1" || field.value.ORG_TYPE === "2",
        ORG_NAME: field.value.ORG_TYPE === "1" ? field.value.SELECTED.HEADER1 : field.value.ORG_NAME,
        FIRST_NAME: field.value.ORG_TYPE === "0" ? field.value.FIRST_NAME : "",
        MIDDLE_NAME: field.value.ORG_TYPE === "0" ? field.value.MIDDLE_NAME : "",
        LAST_NAME: field.value.ORG_TYPE === "0" ? field.value.LAST_NAME : "",
        SUFFIX: field.value.ORG_TYPE === "0" ? field.value.SUFFIX : ""
      }}]
  }
}

const HumanReadableResultCard = ({value}) => (
  <span className='flex-column hr-result'>
    <span className='h1'>{value.HEADER1}</span>
    {value.HEADER2 && <span>{value.HEADER2}</span>}
    {
      value.SECTIONS && value.SECTIONS.map((section, i) => (
        <span className='flex-column' key={i}>
          {section.label && <span className='label'>{section.label}</span>}
          {section.lines && section.lines.map(({line}, k) => (
            <span className="result-value" key={k}>{line}</span>
          ))}
        </span>
      ))
    }
  </span>
)

export class SearchControl extends Field {
  constructor(props = {}){
    super(props);
    this.metadata = props.metadata || {};
    this.multiSelect = props.multiSelect || false;
    this.calculateInitialValue(props)
    this.type = "SearchControl";
    this.fieldClass = "SearchControl";
    this.modalTitle = props.modalTitle || "";
    this.allowedRules = ["searchItemRequired", 'customBusinessRules']
    this.searchType = props.searchType || '';
    this.additionalParameters = props.additionalParameters || {};
    this.parameterFields = props.parameterFields || [];
  }
  calculateInitialValue = props => {
    if(props.value){
      if(this.multiSelect){
        if(Array.isArray(props.value)){
          this.value = props.value
        }else{
          this.value = [];
        }
      }else{
        if(Array.isArray(props.value)){
          this.value = null;
        }else{
          this.value = props.value;
        }
      }
    }else{
      if(this.multiSelect){
        this.value = []
      }else{
        this.value = null;
      }
    }
  }
  getHumanReadableValue(field, value){
    if(field.multiSelect){
      return (
        value.map((result, i) => (
          <div key={i} style={{marginBottom: 10, padding: 0}}>
            <HumanReadableResultCard value={result}/>
          </div>
        ))
      )
    }else{
      return (
        value ?
          <HumanReadableResultCard value={value} />
        :
          ""
      )
    }
  }
  getKeyValuePairs(field){
    return [
      {
        name: field.name,
        value: field.value
      }
    ]
  }
}

export class DependentRadio extends Field {
  constructor(radio = {}){
    super(radio);
    this.type = "dependentRadio";
    this.fieldClass = "DependentRadio";
    this.hasOptionFields = true;
    this.options = radio.options || [{value: "option1", label: "Option 1", fields: []}, {value: "option2", label: "Option 2", fields: []}]
    this.value = radio.value || (this.options.find((option)=>(option.value === this.value)) && radio.value ? radio.value : "0")
    this.selectedIndex = radio.selectedIndex || null;
    this.label = radio.label || "New Dependent Radio";
    this.allowedRules = ["mustBeSelected", 'customBusinessRules']
  }
  getConfirmFieldProps(){
    let confirmFields = [];
    let selectedOption = this.selectedIndex ? this.selectedIndex : 0;
    confirmFields.push({label: this.label, value: this.options[selectedOption].label});
    for(var i = 0; i < this.options[selectedOption].fields.length; i++){
      confirmFields.push({label: this.options[selectedOption].fields[i].label, value: this.options[selectedOption].fields[i].getConfirmFieldProps()[0].value});
    }
    return confirmFields;
  }
  getHumanReadableValue(field, value){
    let selectedOption = field.options.find((option)=>(option.value.toString() === value.toString()));
    return selectedOption ? selectedOption.label : "";
  }
  getKeyValuePairs(field){
    let value = field.value;
    if(value === 'default' || !field.options.find(opt => opt.value === value)){
      value = ''
    }
    return [{name: field.name, value}]
  }
}

export class DependentSelect extends Field {
  constructor(select = {}){
    super(select);
    this.type = "dependentSelect";
    this.fieldClass = "DependentSelect";
    this.hasOptionFields = true;
    this.options = select.options || [{value: "default", label: "[Select an Option]", fields: []}, {value: "option1", label: "Option 1", fields: []}];
    this.value = select.value || (this.options.find((option)=>(option.value === this.value)) && select.value ? select.value : "0");
    this.selectedIndex = select.selectedIndex || 0;
    this.label = select.label || "New Dependent Dropdown";
    this.allowedRules = ["mustBeSelected", 'customBusinessRules'];
  }
  getConfirmFieldProps(){
    let confirmFields = [];
    let selectedOption = this.selectedIndex ? this.selectedIndex : 0;
    confirmFields.push({label: this.label, value: this.options[selectedOption].label});
    for(var i = 0; i < this.options[selectedOption].fields.length; i++){
      confirmFields.push({label: this.options[selectedOption].fields[i].label, value: this.options[selectedOption].fields[i].getConfirmFieldProps()[0].value});
    }
    return confirmFields;
  }
  getHumanReadableValue(field, value){
    let selectedOption = field.options.find((option)=>(option.value.toString() === value.toString()));
    return selectedOption ? selectedOption.label : "";
  }
  getKeyValuePairs(field){
    let value = field.value;
    if(value === 'default' || !field.options.find(opt => opt.value === value)){
      value = ''
    }
    return [{name: field.name, value}]
  }
}

export class LocalSelect extends Select {
  constructor(props = {}){
    super(props)
    this.type = 'LocalSelect'
    this.fieldClass = 'LocalSelect'
    this.options = props.options || []
    this.srcRepeater = props.srcRepeater || '';
    this.srcField = props.srcField || '';
    this.width = props.width || '30%';
    this.allowedRules = ['mustSelectLocalSelect', 'customBusinessRules'];
  }
  getHumanReadableValue(field, value){
    const fields = store.getState().wizard.wizard.fields;
    const srcRepeater = field.srcRepeater ? fields[field.srcRepeater] : null;
    const srcField = field.srcField ? fields[field.srcField] : null;
    if(srcRepeater && srcField){
      const row = srcRepeater.value.find(row => row._rowId === value)
      if(row){
        return new WizardClasses[srcField.fieldClass]().getHumanReadableValue(srcField, row[srcField.fieldId])
      }
    }
    return ''
  }
  getKeyValuePairs(field){
    const fields = store.getState().wizard.wizard.fields;
    const srcRepeater = field.srcRepeater ? fields[field.srcRepeater] : null;
    const srcField = field.srcField ? fields[field.srcField] : null;
    let value = '';
    let meta = {};
    let rowIndex = null;
    if(srcRepeater && srcField){
      const foundIndex = srcRepeater.value.findIndex(row => row._rowId === field.value)
      const row = srcRepeater.value[foundIndex]
      if(row){
        const kvPair = new WizardClasses[srcField.fieldClass]().getKeyValuePairs({...srcField, value: row[srcField.fieldId]})[0]
        rowIndex = foundIndex
        value = kvPair.value
        meta = {displayValue: new WizardClasses[srcField.fieldClass]().getHumanReadableValue({...srcField, value: row[srcField.fieldId]}, row[srcField.fieldId]) }
      }
    }
    return [{value: {value, rowIndex}, name: field.name, meta}]
  }
}

export class DependentCheck extends Field {
  constructor(check = {}){
    super(check);
    this.type = "dependentCheck";
    this.fieldClass = "DependentCheck";
    this.value = check.value || false;
    this.label = check.label || "New Dependent Check";
    this.checkedFields = check.checkedFields || [];
    this.uncheckedFields = check.uncheckedFields || [];
    this.hasCheckFields = true;
    this.hideLabel = true;
    this.allowedRules = ["mustBeTrue", 'customBusinessRules'];
  }
  getConfirmFieldProps(){
    let kvPairs = [];
    kvPairs.push({label: this.label, value: (this.value === true ? "Yes" : "No")});
    if(this.value === true){
      for(let i = 0; i < this.checkedFields.length; i++){
        kvPairs.push({label: this.checkedFields[i].label, value: this.checkedFields[i].getConfirmFieldProps()[0].value});
      }
    }else{
      for(let i = 0; i < this.uncheckedFields.length; i++){
        kvPairs.push({label: this.uncheckedFields[i].label, value: this.uncheckedFields[i].getConfirmFieldProps()[0].value});
      }
    }
    return kvPairs;
  }
  getHumanReadableValue(field, value){
    return value ? "Yes" : "No";
  }
}

const RA_OVERRIDE_TYPE_WITH_DEFAULT = process.env.RA_OVERRIDE_TYPE_WITH_DEFAULT

export const getRegisteredAgentValueObject = (value={MAIL_ADDR: {}}) => ({
  INDIVIDUAL_YN: value.INDIVIDUAL_YN || 'y',
  NEW: value.NEW || false,
  TYPE_ID: RA_OVERRIDE_TYPE_WITH_DEFAULT
    ? process.env.RA_DEFAULT_TYPE
    : value.TYPE_ID || (process.env.RA_NO_DEFAULT_TYPE ? '' : process.env.RA_DEFAULT_TYPE || '461'),
  CRA_ID: value.CRA_ID || '0',
  FIRST_NAME: value.FIRST_NAME || '',
  MIDDLE_NAME: value.MIDDLE_NAME || '',
  LAST_NAME: value.LAST_NAME || '',
  SUFFIX: value.SUFFIX || '',
  ORGANIZATION: value.ORGANIZATION || null,
  ...getAddressObject(value),
  MAIL_ADDR: getAddressObject(value.MAIL_ADDR),
  SELECTED: value.SELECTED || null,
  ...(process.env.RA_EMAIL ? { EMAIL: value.EMAIL || '' } : {}),
  ...(process.env.RA_WEBSITE ? { WEBSITE: value.WEBSITE || '' } : {})
})

export const resolveAgentMailingAddress = value => ({
  ...((value && value.MAIL_ADDR) || getRegisteredAgentValueObject().MAIL_ADDR),
  STATE: ((value && value.MAIL_ADDR && value.MAIL_ADDR.STATE) || process.env.RA_MAILING_DEFAULT_STATE || '')
})
const RA_TYPE_SELECTION_DEFAULT = process.env.RA_TYPE_SELECTION_DEFAULT;

export class RegisteredAgentSearch extends AddressBlock{
  constructor(props = {}){
    super(props);
    this.type = "registeredAgentSearch";
    this.fieldClass = "RegisteredAgentSearch";
    this.value = {
      ...getRegisteredAgentValueObject(props.value),
      MAIL_ADDR: resolveAgentMailingAddress(props.value)
    }
    if(this.value.MAIL_ADDR.COUNTRY === '')this.value.MAIL_ADDR.COUNTRY = 'United States';
    this.selected = props.selected || null;
    this.label = props.label || "Registered Agent";
    this.sectionLabel = props.sectionLabel || '';
    this.hideLabel = true;
    this.hideTooltip = true;
    this.showCounty = false;
    this.showCountry = false;
    this.disableState = props.disableState === false ? false : true;
    this.disableMailingState = props.disableMailingState || false;
    this.searchPlaceholder = props.searchPlaceholder || "(Search by name of individual or organization)";
    this.showMailingAddress = props.showMailingAddress || false;
    this.agentSelected = props.agentSelected || false;
    this.allowedRules = [
      "registeredAgentIsRequired",
      "registeredAgentNotSoS",
      "registeredAgentAddressIsValid",
      "poBoxNotAllowed",
      'checkForRegulatedWords',
      'customBusinessRules'
    ]
    this.allowedMasks = ["addressUpperCase"];
    this.defaultState = process.env.RA_MAILING_DEFAULT_STATE || props.defaultState || '';
    this.defaultMailState = process.env.RA_MAILING_DEFAULT_STATE || props.defaultMailState || '';
    this.addingAgent = props.addingAgent || false;
    this.allowInfoMessage = false;
    this.agentType1Label = props.agentType1Label || ''
    this.agentType2Label = props.agentType2Label || ''
    this.modalOrgLabel = props.modalOrgLabel || ''
    this.modalShowSuffix = props.modalShowSuffix || false
    this.typeSelectionDisabled = props.typeSelectionDisabled ?  props.typeSelectionDisabled  : (RA_TYPE_SELECTION_DEFAULT || false);
    // this.template = props.template || [];
  }
  formatAddress(value){
    return (
      <span>
        {value.ADDR1}{value.ADDR1 !== "" ? <br/> : null}
        {value.ADDR2}{value.ADDR2 !== "" ? <br/> : null}
        {value.ADDR3}{value.ADDR3 !== "" ? <br/> : null}
        {value.CITY + (value.CITY !== "" && (value.STATE !== "" || value.POSTAL_CODE !== "") ? ', ' : "") + value.STATE + (value.STATE !== "" && value.POSTAL_CODE !== "" ? ', ' : "") + value.POSTAL_CODE}{value.CITY != "" || value.STATE != "" || value.POSTAL_CODE != "" ? <br/> : null}
        {this.showCounty && value.COUNTY !== "" ? value.COUNTY : null}{value.COUNTY !== "" ? <br/> : null}
        {this.showCountry && value.COUNTRY !== "" ? value.COUNTRY : null}
      </span>
    )
  }
  getByType = (type, value) => {
    switch (type) {
      case 'address':
        return value ? <FormattedAddress value={value} className='value' /> : "None"
      case 'h1':
        return <span className='primary'>{value}</span>
      case 'h2':
        return <span className='secondary'>{value}</span>
      case 'subtitle':
        return <span className="subtitle">{value}</span>
      case 'line':
        return <span className=''>{value}</span>
      default:
        return <span>{value}</span>
    }
  }
  getHumanReadableValue(field, value){
    return (value.CRA_ID.toString() !== "0" && value.SELECTED ?
      value.SELECTED ?
        <span className='confirm-result'>
          <span className='row'>{this.getByType("h1", value.SELECTED.HEADER1)}</span>
          <span className='row'>{this.getByType("subtitle", value.SELECTED.HEADER2)}</span>
          {
            value.SELECTED.SECTIONS.map((section, i) => (
              <React.Fragment key={i}>
                <span className='row'>{this.getByType('h2', section.label)}</span>
                {
                  section.lines && section.lines.map(({line}, j) => (
                    <span className='row' key={j}>{this.getByType("line", line)}</span>
                  ))
                }
              </React.Fragment>
            ))
          }
          {/* {
            Array.isArray(field.selected.secondary) ?
            field.selected.secondary.map((line, i) => (<span className="row" key={i}>{line}</span>))
            :
            <span className="row">{field.selected.secondary}</span>
          } */}
        </span> : null
      :
        value.ORGANIZATION || value.FIRST_NAME || value.MIDDLE_NAME || value.LAST_NAME || value.SUFFIX ?
        <span className='confirm-result'>
          <span className="row">Noncommercial Agent</span>
          <span className="row">
            {
              value.INDIVIDUAL_YN === "y" || value.INDIVIDUAL_YN === true ?
              value.FIRST_NAME + (value.MIDDLE_NAME !== "" ? ` ${value.MIDDLE_NAME} ` : " ") + value.LAST_NAME + (value.SUFFIX ? ` ${value.SUFFIX}` : '')
              :
              <React.Fragment>
                {value.ORGANIZATION.HEADER1}
                {value.ORGANIZATION.HEADER2 && (
                  <React.Fragment>
                    <br/>
                    {value.ORGANIZATION.HEADER2}
                  </React.Fragment>
                )}
              </React.Fragment>
            }
          </span>
          {
            field.showMailingAddress &&
            <span className="row secondary">Physical Address:</span>
          }
          <span className={`row`}>{this.formatAddress(value)}</span>
          {
            field.showMailingAddress &&
            <React.Fragment>
              <span className="row secondary">Mailing Address:</span>
              <span className="row">{this.formatAddress(value.MAIL_ADDR)}</span>
            </React.Fragment>
          }
        </span>
        :
        <span>
          <span>None</span>
        </span>
    )
  }
  getKeyValuePairs(field){
    return [{name: field.name, value: {...field.value, SOURCE_ID: (field.value.SELECTED && field.value.SELECTED.SOURCE_ID) || 0}}]
  }

}

export class ServerSelect extends Select{
  constructor(props = {}){
    super(props);
    this.type = "serverSelect";
    this.fieldClass = "ServerSelect";
    this.controlType = "select";
    this.srcHandler = props.srcHandler || null;
    this.placeholder = props.placeholder || "None";
    this.value = props.value || "0";
    this.selectedOptionLabel = props.selectedOptionLabel || this.value;
    this.optionsSrc = props.optionsSrc || "0";
    this.subOptionsSrc = props.subOptionsSrc || "";
    this.optionsSrcOverride = props.optionsSrcOverride || '';
    this.subOptionsSrcOverride = props.subOptionsSrcOverride || '';
    this.lastSeenOptionsSrcOverride = props.lastSeenOptionsSrcOverride || '';
    this.lastSeenSubOptionsSrcOverride = props.lastSeenSubOptionsSrcOverride || '';
    this.allowedRules = ["mustBeSelected", 'customBusinessRules']
    this.optionsFetched = props.optionsFetched || false;
  }
  getKeyValuePairs(field){
    const selectedOption = field.options.find(opt => opt.value === field.value)
    const displayValue = selectedOption ? selectedOption.label : undefined;
    return[{name: field.name, value: field.value, meta: {displayValue}}]
  }
}

export class ServerRadio extends ServerSelect{
  constructor(props = {}){
    super(props);
    this.type = "serverRadio";
    this.controlType = "radio";
    this.fieldClass = "ServerRadio";
    this.options = props.options || [{value: "option1value", label: "Option 1"}]
    this.allowedRules = ["mustBeSelected", 'customBusinessRules']
  }
  getKeyValuePairs(field){
    const selectedOption = field.options.find(opt => opt.value === field.value)
    const displayValue = selectedOption ? selectedOption.label : undefined;
    return[{name: field.name, value: field.value, meta: {displayValue}}]
  }
}

export class Paragraph extends Field{
  constructor(props = {}){
    super(props);
    this.type = "paragraph";
    this.fieldClass = "Paragraph";
    this.value = props.value || "Lorum ipsum delorum...";
    this.textAlign = props.textAlign || "left";
    this.webVisible = props.webVisible === false ? false : true;
    this.isValid = true;
    this.label = "";
    this.width = props.width || "100%";
    this.confirm = false;
    this.hideLabel = true;
    this.displayType = props.displayType || 'markdown';
    this.codes = props.codes || {};
    this.scrollable = props.scrollable || false;
    this.maxHeight = props.maxHeight || 300;
  }
  getHumanReadableValue = (field, value) => {
    return <ReactMarkdown source={value} className='markdown' />
  }
}

const repeaterRowValidationRule = {fn: 'validateRepeaterFields', ruleId: 'validateRepeaterFields', props: {}}

export class Repeater extends Field {
  constructor(props = {}) {
    super(props)
    this.type = 'repeater'
    this.fieldClass = 'Repeater'
    this.label = props.label || ''
    this.template = props.template || []
    this.value = props.value || []
    this.metadata = props.metadata || {}
    this.hideLabel = true
    this.ownsTooltip = true
    this.allowedRules = [
      'repeaterMinRows',
      'repeaterMaxRows',
      'mayOnlyAddAdditionalRows',
      'repeaterColumnsMustBeUnique',
      'enforceRepeaterChildConstraints',
      'mustHaveSameNumRowsAs',
      'validateRepeaterFields',
      'minRepeaterEdit',
      'minRepeaterDelete',
      'customBusinessRules'
    ]
    this.businessRules = props.businessRules
      ? this.resolveBusinessRules(props.businessRules)
      : [repeaterRowValidationRule]
    this.rowErrors = props.rowErrors || null
    this.emptyRepeaterLabel = props.emptyRepeaterLabel || ''
    this.displayType = props.displayType || ''
    this.disableAdding = props.disableAdding || false
    this.disableCloning = props.disableCloning || false
    this.disableDeleting = props.disableDeleting || false
    this.disableEditing = props.disableEditing || false
    this.printMailRequiredIf = props.printMailRequiredIf || 'never'
    this.trackChanges = props.trackChanges || false
    this.showChangesOnForm = props.showChangesOnForm || false
    this.hideChangesStylingOnForm = props.hideChangesStylingOnForm || false;
    this.hideDeletedRowsOnForm = props.hideDeletedRowsOnForm || false;
    this.customDeleteLabel = props.customDeleteLabel || "";
    this.noDeletedStyling = props.noDeletedStyling || false;
    this.showChangesOnSummary = props.showChangesOnSummary === false ? false : true;
    this.noDeletedStylingOnSummary = props.noDeletedStylingOnSummary || false;
  }
  resolveBusinessRules = rules => {
    if (rules) {
      return rules
    } else {
      return [repeaterRowValidationRule]
    }
  }
  getConfirmFieldProps() {
    return [{ label: this.label, value: this.value }]
  }
  getFieldsSnapshot = (fields, row) => ({
    ...fields,
    ...Object.keys(row).reduce((obj, fieldId) => ({
      ...obj,
      [fieldId]: {
        ...fields[fieldId],
        value: row[fieldId]
      }
    }), {})
  })
  getFormattedRows = (rows, fields) => {
    // This next variable is confusingly named, but it's just to preserve the meta rows in the value output itself
    const metaRowsForValue = []
    const metaRows = []
    const ROWS = rows.map((row, i) => {
      let value = {}
      let meta = {}
      const snapshot = this.getFieldsSnapshot(fields, row)
      Object.keys(row)
        .filter(key => key.charAt(0) !== '_')
        .forEach(fieldId => {
          const tempField = fields[fieldId];
          if(resolveFieldConditions(tempField.conditions, snapshot, tempField.conditionsOperator)){
            const kvPair = new WizardClasses[fields[fieldId].fieldClass]().getKeyValuePairs({
              ...fields[fieldId],
              value: row[fieldId]
            })[0]
            value[fields[fieldId].name] = kvPair.value
            meta[fields[fieldId].name] = kvPair.meta
          }
        })
      const statusId = row._statusId ? row._statusId : row._statusId === 0 ? 0 : 1;
      value._statusId = statusId;
      // This is to store metadata about the row in the value for this row
      const rowMeta = Object.keys(row).reduce((obj, key) => {
        //if(key.charAt(0) === "_"){
        if(key === '_statusId' || key === '_rowId' || key === '_original' || key === '_partyId') {
          obj[key] = row[key];
        }
        return obj;
      }, {})
      metaRowsForValue.push(rowMeta)
      metaRows.push(meta)
      return value
    })

    return { ROWS, metaRows, metaRowsForValue }
  }
  getKeyValuePairs(field, fields) {
    const { ROWS, metaRows, metaRowsForValue } = this.getFormattedRows(field.value, fields)
    const originalFormattedRows = this.getFormattedRows(
      store.getState().wizard.diff.originalByFieldId[field.fieldId] || [],
      fields
    )
    const meta = {
            original: {
              ROWS: originalFormattedRows.ROWS,
              ROWS_META: originalFormattedRows.metaRowsForValue
            },
            metaRows,
            originalMetaRows: this.getFormattedRows(
              store.getState().wizard.diff.originalByFieldId[field.fieldId] || [],
              fields
            ).metaRows
          }
    return [
      {
        name: field.name,
        value: { ROWS, METADATA: field.metadata, ROWS_META: metaRowsForValue },
        meta
      }
    ]
  }
  static getPublicProperties(rows){
    return rows.map(row => (
      Object.entries(row).reduce((obj, [key, value]) => {
        if(key.charAt(0) !== "_") obj[key] = value;
        return obj;
      }, {})
    ))
  }
  static compareValues(originalValue, currentValue){
    return !deepEqual(this.getPublicProperties(originalValue), this.getPublicProperties(currentValue))
  }

}

export class DataTable extends Field{
  constructor(props={}){
    super(props);
    this.type = 'DataTable'
    this.fieldClass = 'DataTable'
    this.value = props.value || {template: [], rows: []}
    this.dataSrc = props.dataSrc || '';
    this.includeInSummary = props.includeInSummary === true ? true : false;
    this.propagateValueToServer = props.propagateValueToServer || false;
    this.formDisplayType = props.formDisplayType || 'newLine';
  }
  getKeyValuePairs(field, fields){
    if(field.propagateValueToServer){
      return [{name: field.name, value: field.value}]
    }else{
      return [{name: field.name, value: null}]
    }
  }
  getHumanReadableValue = () => null;
}

export class NameSearch extends ConfirmFields{
  constructor(props = {}){
    super(props);
    this.type = "nameSearch"
  }
}

export class Summary extends Field{
  constructor(props={}){
    super(props)
    this.type = "Summary";
    this.fieldClass = "Summary";
    this.confirm = false;
    this.hideLabel = true;
    this.allowedRules = []
    this.includeInSummary = false;
  }
}

const getUploadAndSubmitValue = () => ({method: 'online', files: {}, signers: [], attestations: []})

export class UploadAndSubmit extends Field {
  constructor(props = {}) {
    super(props)
    this.type = 'UploadAndSubmit'
    this.label = 'Upload & Submit'
    this.fieldClass = 'UploadAndSubmit'
    this.name = props.name || 'UPLOADS'
    this.confirm = false
    this.hideLabel = true
    this.allowedRules = ['enforceUploadPlaceholderRules', 'validateRepeaterFields', 'customBusinessRules']
    this.value = { ...getUploadAndSubmitValue(), ...props.value }
    this.viewOnly = props.viewOnly || false
    this.businessRules = [
      { fn: 'enforceUploadPlaceholderRules', ruleId: 'enforceUploadPlaceholderRules', props: {} },
      {
        fn: 'validateThirdPartySignaturesRepeater',
        ruleId: 'validateThirdPartySignaturesRepeater',
        props: {}
      }
    ]
    this.delegateErrorsToChildren = true
    this.onlineInstructions = props.onlineInstructions || ''
    this.mailInstructions = props.mailInstructions || ''
    this.signerInstructions = props.signerInstructions || ''
    this.allowOnline = props.allowOnline === false ? false : true
    this.allowPrint = props.allowPrint === false ? false : true
    this.allowSigners = props.allowSigners === false ? false : true
    this.includeInSummary = false
    this.excludeFromDiff = true
    this.hideFieldOnForm = true
    this.onlineTabLabel = props.onlineTabLabel || ''
    this.printTabLabel = props.printTabLabel || ''
    this.signerTabLabel = props.signerTabLabel || ''
    this.onlineButtonLabel = props.onlineButtonLabel || ''
    this.printButtonLabel = props.printButtonLabel || ''
    this.signerButtonLabel = props.signerButtonLabel || ''
    this.template = props.template || []
    this.allowMultipleSigners = props.allowMultipleSigners || false
    this.signerAttestations = props.signerAttestations || [] 
    this.signatureTextLabel = props.signatureTextLabel || ''
    this.signatureDateLabel = props.signatureDateLabel || ''
    this.showSignatureAttestations = props.showSignatureAttestations || false
  }
  getKeyValuePairs(field, fields) {
    let files = {}
    let allowedFiles = getUploadsFromFields(fields).map(field => field.fieldId)
    Object.keys(field.value.files || {})
      .filter(fieldId => allowedFiles.includes(fieldId)) //Skip any deleted repeater rows
      .forEach(fieldId => {
        files[fieldId] = field.value.files[fieldId]
          .map(file => {
            const newFile = {
              ...file,
              filename: file.filename,
              type: file.type
            }
            delete newFile.file
            return newFile
          })
          .filter(file => file.status === 'success')
          .filter(file => file)
      })
    return [
      {
        name: field.name,
        value: {
          ...field.value,
          files,
          method:
            store.getState().wizard.requirePrintMailMethod.size > 0 ? 'mail' : field.value.method
        }
      }
    ]
  }
  getHumanReadableValue(field, value) {
    return value.method === 'online' ? 'Pay & File Online' : 'Print & Mail'
  }
}

export class Signatures extends Field{
  constructor(props={}){
    super(props)
    this.type = "Signatures";
    this.fieldClass = "Signatures";
    this.label = props.label || "Signatures"
    this.name = props.name || "SIGNATURES";
    this.hideLabel = true;
    this.value = props.value || (process.env.SIGNATURE_DEFAULT_DATE ? [{name: '', sig: '', date: format(new Date(), 'MM/dd/yyyy')}] : []);
    this.srcRepeater = props.srcRepeater || '';
    this.srcField = props.srcField || '';
    this.delegateErrorsToChildren = true;
    this.emptyText = props.emptyText || '';
    this.allowedRules = ['allSignaturesRequired', 'minRequiredSignatures', 'enforceSignatureDateRange', 'checkForRegulatedWords', 'customBusinessRules', 'signaturesMinLength'];
    this.requireWebFileIfMultiple = props.requireWebFileIfMultiple || false;
    this.multiple = props.multiple === false ? false : true;
    this.todayButton = props.todayButton === false ? false : true;
    this.showCapacity = props.showCapacity || false;
    this.showOnBehalfOf = props.showOnBehalfOf === false ? false : true;
    this.capacityFreeText = props.capacityFreeText || false;
    this.allowedMasks = ["onlyLetters"]
  }
  getKeyValuePairs(field, fields){
    if(field.multiple){
      const currentRepeaterRows = (fields[field.srcRepeater] || {}).value;
      if(field.value.length === currentRepeaterRows.length){
        return [{name: field.name, value: field.value}]
      }else{
        const value = currentRepeaterRows.map((c, i) => (field.value[i] ? field.value[i] : {sig: '', date: null}))
        return [{name: field.name, value}]
      }
    }else{
      return [{name: field.name, value: field.value}]
    }
  }

}

const getFormationLocaleValueObject = () => ({FORMATION_TYPE: '', STATE: '', COUNTRY: '', OTHER: ''})


export class FormationLocale extends Field{
  constructor(props={}){
    super(props)
    this.type = 'FormationLocale';
    this.fieldClass = 'FormationLocale';
    this.label = props.label || 'Formation Locale'
    this.name = props.name || 'FORMATION_INFO';
    this.value = props.value || getFormationLocaleValueObject();
    this.allowedRules = ['formationLocaleRequired', 'checkForRegulatedWords', 'customBusinessRules']
    this.excludeCurrentState = props.excludeCurrentState || false;
  }
  getHumanReadableValue = (field, value) => {
    switch (value.FORMATION_TYPE) {
      case 'STATE':
        return value.STATE;
      case 'COUNTRY':
        return value.COUNTRY;
      case 'OTHER':
        return value.OTHER;
    }
  }
getKeyValuePairs = (field, fields) => {
    return [{name: field.name, value: {FORMATION_TYPE: field.value.FORMATION_TYPE, FORMATION_LOCALE: field.value[field.value.FORMATION_TYPE]}}]
  }

}

const getUCCSearchValue = (props = {}) => ({
  SEARCH_REQUEST_TYPE: props.SEARCH_REQUEST_TYPE || 'LIST_ONLY',
  SEARCH_LAST_NAME: props.SEARCH_LAST_NAME || '',
  SEARCH_FIRST_NAME: props.SEARCH_FIRST_NAME || '',
  SEARCH_MIDDLE_NAME: props.SEARCH_MIDDLE_NAME || '',
  SEARCH_CITY: props.SEARCH_CITY || '',
  SEARCH_SUFFIX: props.SEARCH_SUFFIX || '',
  SEARCH_STATE: props.SEARCH_STATE || '',
  SEARCH_ORG_NAME: props.SEARCH_ORG_NAME || '',
  SEARCH_UCC_NUM: props.SEARCH_UCC_NUM || '',
  SEC_PARTY_SEARCH_YN: props.SEC_PARTY_SEARCH_YN || false,
  SEARCH_LAPSED: props.SEARCH_LAPSED || false,
  SEARCH_UCC: props.SEARCH_UCC || false,
  SEARCH_GOV: props.SEARCH_GOV || false,
  SEARCH_EFS: props.SEARCH_EFS || false,
  SEARCH_CROP_LIEN: props.SEARCH_CROP_LIEN || false,
  SEARCH_AG_LIEN: props.SEARCH_AG_LIEN || false,
  IS_PARTIAL: props.IS_PARTIAL || false,
  IS_ORG: props.IS_ORG || false,
  SEARCH_TYPE: props.SEARCH_TYPE || 'DEBTOR',
  BEGIN_DATE: props.BEGIN_DATE || ''
})

export class UCCSearch extends Field {
  constructor(props={}) {
    super(props)
    this.type = 'UCCSearch'
    this.fieldClass = 'UCCSearch'
    this.allowedRules = ['uccSearchRequired', 'atLeastOneUccSearchResultIsRequired', 'customBusinessRules']
    this.allowedMasks = []
    this.label = ''
    this.value =
      props.value && typeof props.value === 'object'
        ? { cache: props.value.cache, ids: props.value.ids, search: getUCCSearchValue(props.value.search) }
        : { cache: null, ids: null, search: getUCCSearchValue() }
    this.uccMode = props.uccMode || 11;
    this.displaySearchTypeOnForm = props.displaySearchTypeOnForm !== undefined
      ? props.displaySearchTypeOnForm
      : process.env.UCC_SEARCH_ALLOW_SEARCH_TYPE === true
      ? true
      : false;
    this.skipResultSelection = props.skipResultSelection || false;
  }
  getHumanReadableValue = (field, value) => {
    return (
      field.value.cache ?
        <React.Fragment>
          <UCCSearchDisplay value={value.search} uccMode={field.uccMode} />
          <div style={{width: '100%', overflowX: 'auto', padding: 0}}>
            <table className="styled-table">
              <thead>
                <tr>
                  {field.value.cache.template.map(({ label }, i) => (
                    <th key={i}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {field.value.cache.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, i) => (
                      <td key={i}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </React.Fragment>
      : null
    )
  }
  getKeyValuePairs = field => {
    const isOrg = field.value.search.IS_ORG === 'true' || field.value.search.IS_ORG === true
    return [
      {
        name: field.name,
        value: {
          ...field.value,
          search: {
            ...field.value.search,
            SEARCH_LAST_NAME: isOrg ? '' : field.value.search.SEARCH_LAST_NAME,
            SEARCH_FIRST_NAME: isOrg ? '' : field.value.search.SEARCH_FIRST_NAME,
            SEARCH_MIDDLE_NAME: isOrg ? '' : field.value.search.SEARCH_MIDDLE_NAME,
            SEARCH_SUFFIX: isOrg ? '' : field.value.search.SEARCH_SUFFIX,
            SEARCH_ORG_NAME: isOrg ? field.value.search.SEARCH_ORG_NAME : '',
          },
          SEARCH_MODE: field.uccMode,
          SEC_PARTY_SEARCH_YN: false
        }
      }
    ]
  }
}

export class MultiComplete extends Field {
  constructor(props={}){
    super(props)
    this.type = 'MultiComplete'
    this.fieldClass = 'MultiComplete'
    this.allowedRules = ['minMultiComplete', 'customBusinessRules']
    this.options = props.options || []
    this.value = props.value || []
    this.maxRows = props.maxRows || null;
    this.width = props.width || '40%'
    this.ownsLabel = true;
    this.stackedSummary = props.stackedSummary ? props.stackedSummary : false;
    this.leaveOpenOnSelect = props.leaveOpenOnSelect || false;
    this.allowedRepeaterConstraints = ["maxNumberOfSelectedOption", "minNumberOfSelectedOption"];
  }
  getHumanReadableValue = (field, value) => (
    value.map(v => (field.options.find(option => option.value === v) || {}).label || v).join(field.stackedSummary ? ',\n' : ', ')
  )
}

export class ServerMultiComplete extends ServerSelect {
  constructor(props = {}) {
    super(props)
    this.type = 'ServerMultiComplete'
    this.fieldClass = 'ServerMultiComplete'
    this.allowedRules = ['minMultiComplete', 'customBusinessRules']
    this.options = props.options || []
    this.value = props.value || []
    this.maxRows = props.maxRows || null
    this.width = props.width || '40%'
    this.controlType = 'ServerMultiComplete'
    this.ownsLabel = true
    this.stackedSummary = props.stackedSummary ? props.stackedSummary : false
    this.leaveOpenOnSelect = props.leaveOpenOnSelect || false
  }
  getHumanReadableValue = (field, value) =>
    value
      .map((v) => (field.options.find((option) => option.value === v) || {}).label || v)
      .join(field.stackedSummary ? ',\n' : ', ')
  getKeyValuePairs = (field, value) => {
    const displayValue = field.value
      .map((v) => (field.options.find((o) => o.value === v) || {}).label || '')
      .filter((x) => x !== '')
      .join(', ')
    return [{ name: field.name, value: field.value, meta: { displayValue } }]
  }
}

const getEfsCropPickerValue = (value = {}) => ({
  yearType: value.yearType || 'all',
  startYear: value.startYear || '',
  endYear: value.endYear || '',
  years: value.years || []
})

export class EFSCropPicker extends Field{
  constructor(props={}){
    super(props)
    this.type = 'EFSCropPicker'
    this.fieldClass = 'EFSCropPicker'
    this.label = props.label || 'Crop years';
    this.allowedRules = ['efsRequired', 'checkForRegulatedWords', 'customBusinessRules']
    this.allowedMasks = []
    this.value = getEfsCropPickerValue(props.value)
    this.ownsLabel = true;
  }
  getHumanReadableValue = (field, value) => {
    switch (value.yearType) {
      case 'all': return 'All years'
      case 'none': return process.env.EFS_CROP_PICKER_NONE_LABEL || 'None'
      case 'duration': return `${value.startYear} - ${value.endYear}`
      case 'arbitrary': return value.years.join(', ')
      default: return ''
    }
  }
}

export function CreateInstancesFromJson(json, preloadedData, props = {}){
  var parsed = {stepOrder: json.stepOrder, steps: {}, sections: {}, fields: {}};

  let repairedJson = repairJson(deepCopy(json))
  if(
    !deepEqual(repairedJson.fields, json.fields) ||
    !deepEqual(repairedJson.sections, json.sections) ||
    !deepEqual(repairedJson.steps, json.steps)
  ){
    console.warn("While populating this wizard some extraneous ids were found and removed.")
  }
  json = repairedJson;

  Object.keys(json.steps).forEach((stepId) => {parsed.steps[stepId] = new Step(json.steps[stepId])})
  Object.keys(json.sections).forEach((sectionId) => {parsed.sections[sectionId] = new Section(json.sections[sectionId])})
  //let fields = Object.keys(json.fields).map((id)=>(json.fields[id]));
  Object.keys(json.fields).forEach((fieldId) => {parsed.fields[fieldId] = getFieldClass(json.fields[fieldId], preloadedData, json.fields, props)})
  Object.keys(parsed.fields).forEach(fieldId => {
    parsed.fields[fieldId] = postParseProcess(parsed.fields[fieldId], parsed.fields, json.fields[fieldId])
  })
  return parsed;
}

function getFieldClass(field, preloadedData={}, fields, props){
  const valueIsDerivedFromFieldId = field.disabled && !field.hideFieldOnForm
  const valueSource = valueIsDerivedFromFieldId ? field.fieldId : field.name
  const altValueSource = valueIsDerivedFromFieldId ? field.name : field.fieldId
  const propExists =
    (preloadedData[valueSource] !== undefined || preloadedData[altValueSource] !== undefined) &&
    (!field.prepopulationDisabled ||
      (field.allowCorrectionPopulation && props.correctionMode) ||
      props.forceAllowPrepopulation)
  const derivedValue = valueIsDerivedFromFieldId
    ? preloadedData[field.fieldId] !== undefined
      ? preloadedData[field.fieldId]
      : preloadedData[field.name]
    : preloadedData[field.name]
  if(propExists){
    field.value = derivedValue;
  }
  switch (field.type) {
    case "text":
    case "number":
      return new Field(field);
    case "textArea":
      return new TextArea(field);
    case "paragraph":
      field.codes = getMergeCodesFromString(field.value)
      return new Paragraph(field);
    case "file":
      return new FileUpload(field);
    case "checkbox":
      return new Checkbox(field);
    case "date":
      const parsedDate = field.value ? parseISO(field.value) : null;
      if(parsedDate && isValid(new Date(parsedDate))){
        field.value = format(parsedDate, "MM/dd/yyyy")
      }
      return new DateInput(field);
    case "DateRange":
      return new DateRange(field);
    case 'TimeInput':
      return new TimeInput(field);
    case "radio":
      field.selectedIndex = field.options.findIndex(option=>(option.value === field.value))
      field.value = (field.value !== undefined ? field.value : '').toString();
      return new Radio(field);
    case "select":
      field.selectedIndex = field.options.findIndex(option=>(option.value === field.value))
      field.value = (field.value || "").toString();
      return new Select(field);
    case "confirmFields":
      if(propExists){
        field.value={val1: derivedValue, val2: derivedValue}
      }
      return new ConfirmFields(field);
    case "addressBlock":
      return new AddressBlock(field);
    case "dependentRadio":
      field.selectedIndex = field.options.findIndex(option=>(option.value === field.value))
      field.value = field.value.toString();
      return new DependentRadio(field);
    case "dependentSelect":
      field.selectedIndex = field.options.findIndex(option=>(option.value === field.value))
      field.value = field.value.toString();
      return new DependentSelect(field);
    case "dependentCheck":
      return new DependentCheck(field);
    case "registeredAgentSearch":
      if(field.value){
        field.selected = field.value.selected;
        delete field.value.selected;
      }
      return new RegisteredAgentSearch(field);
    case "serverSelect":
      field.selectedIndex = field.options.findIndex(option=>(option.value === field.value))
      field.value = (field.value || "").toString()
      return new ServerSelect(field);
    case "serverRadio":
      field.selectedIndex = field.options.findIndex(option=>(option.value === field.value))
      field.value = field.value.toString()
      return new ServerRadio(field);
    case "DataTable":
      return new DataTable(field);
    case "repeater":{
      if (propExists) {
        let rows = [];
        let value = field.value.ROWS || [];
        value.forEach(row => {
          let newRow = {};
          const rowSnapshot = field.template.reduce((obj, key) => ({...obj, [key]: row[fields[key].name]}),{});
          const snapshot = new Repeater().getFieldsSnapshot(fields, rowSnapshot)
          field.template.forEach(fieldId => {
            let fieldClass = fields[fieldId];
            let cellName = fieldClass.name;
            newRow[fieldId] = fieldClass
              ? fieldClass.prepopulationDisabled && !props.forceAllowPrepopulation && !(fieldClass.allowCorrectionPopulation && props.correctionMode)
                ? getFieldClass({ ...fieldClass }, {}, fields, props).value
                : getFieldClass({
                  ...fieldClass,
                  ...(
                    resolveFieldConditions(
                      fields[fieldId].conditions,
                      snapshot,
                      fields[fieldId].conditionsOperator
                    ) ?
                    {value: row[cellName]}
                    : {}
                  )}, {}, fields, props).value
              : row[cellName];
          });
          newRow._statusId = row._statusId ? row._statusId : 0;
          newRow._rowId = shortid.generate();
          newRow._original = true;
          newRow = {...newRow, ...(Object.keys(row).reduce((obj, key) => {
            //if(key.charAt(0) === "_"){
            if(key === '_statusId' || key === '_rowId' || key === '_original' || key === '_partyId') {
              obj[key] = row[key];
            }
            return obj;
          }, {}))}
          rows.push(newRow);
        });
        field.value = rows;
      }
      return new Repeater(field);
    }
    case "nameSearch":
      if(propExists){
        field.value={val1: derivedValue[0], val2: derivedValue[0]}
      }
      return new NameSearch(field);
    case "Summary":
      return new Summary(field);
    case "FullName":
      return new FullName(field);
    case "NameOrg":
      field.value = field.value ? {...field.value, ORG_YN: typeof field.value.ORG_YN === "string" ? field.value.ORG_YN === "Y" ? true : false : field.value.ORG_YN} : undefined
      return new NameOrg(field)
    case "NameQualifiedOrg":
      field.value = field.value ? {...getNameQualifiedOrgObject(), ...field.value, ORG_YN: field.value.ORG_YN === "Y" ? true : false} : getNameQualifiedOrgObject()
      // field.selected = field.value.selected;
      // delete field.value.selected;
      return new NameQualifiedOrg(field)
    case "SearchControl":
      return new SearchControl(field)
    case "UploadPlaceholder":
      return new UploadPlaceholder(field);
    case "UploadAccumulator":
      return new UploadAccumulator(field);
    case "UploadAndSubmit":
      return new UploadAndSubmit(field);
    case "Signatures":
      if(props.clearSignatures){
        field.value = new Signatures().value
      }
      return new Signatures(field);
    case "FormationLocale":
      field.value = {...getFormationLocaleValueObject(), [field.value.FORMATION_TYPE]: field.value.FORMATION_LOCALE, FORMATION_TYPE: field.value.FORMATION_TYPE}
      return new FormationLocale(field);
    case "EntityNameSearch":
      return new EntityNameSearch(field)
    case "UCCSearch":
      return new UCCSearch(field)
    case "LocalSelect":
      return new LocalSelect({...field, value: (field.value || {}).value || ''})
    case "MultiComplete":
      return new MultiComplete(field)
    case "ServerMultiComplete":
      return new ServerMultiComplete(field)
    case "EFSCropPicker":
      return new EFSCropPicker(field)
    default:
      return new Field(field);
  }
}

const postParseProcess = (field, fields, originalField) => {
  switch (field.type) {
    case 'LocalSelect':
      if(field.value){
        const srcRepeater = fields[field.srcRepeater]
        const srcField = fields[field.srcField]
        if(srcRepeater && srcField && typeof originalField.value === 'object'){
          return {...field, value: srcRepeater.value[originalField.value.rowIndex] ? srcRepeater.value[originalField.value.rowIndex]._rowId : ''}
        }else{
          return field;
        }
      }else{
        return field
      }
    default:
      return field;
  }
}

const repairJson = ({steps, sections, fields, stepOrder}) => {
  // Purge undefined sections
  Object.keys(steps).forEach(stepId => {
    let step = steps[stepId]
    step.sectionOrder = step.sectionOrder.filter(sectionId => (sections[sectionId]))
    steps[stepId] = step;
  })
  // Purge undefined fields
  Object.keys(sections).forEach(sectionId => {
    let section = sections[sectionId]
    section.fieldOrder = section.fieldOrder.filter(fieldId => (fields[fieldId]))
    sections[sectionId] = section;
    // Fix fields whose parents are a section, but are incorrectly classified.
    section.fieldOrder.forEach(fieldId => {
      const field = fields[fieldId];
      const fieldParentType = ((field.parent || {}).type || "").toUpperCase();
      if(fieldParentType !== 'SECTION'){
        console.warn(`While populating this wizard the following field with an incorrectly classified parent, was identified and corrected.`, field)
        fields = {
          ...fields,
          [fieldId]: {
            ...field,
            parent: {
              ...field.parent,
              type: 'SECTION',
              id: sectionId
            }
          }
        }
      }
    })
  })
  // Purge undefined subfields
  Object.keys(fields).forEach(fieldId => {
    let field = fields[fieldId]
    if(field.hasOptionFields){
      field.options = field.options.map(option => {
        option.fields = option.fields.filter(subfieldId => (fields[subfieldId]))
        return option;
      })
    }
    if(field.hasCheckFields){
      field.checkedFields = field.checkedFields.filter(subfieldId => (fields[subfieldId]))
      field.uncheckedFields = field.uncheckedFields.filter(subfieldId => (fields[subfieldId]))
    }
    if(field.template){
      field.template = field.template.filter(subfieldId => (fields[subfieldId]))
    }
    fields[fieldId] = field;
  })
  // Purge unrenderable fields
  const renderableFields = stepOrder
    .reduce((newSections, stepId) => ([...newSections, ...steps[stepId].sectionOrder]), [])
    .reduce((newfields, sectionId) => ([...newfields, ...sections[sectionId].fieldOrder]), [])
  fields = Object.keys(fields).reduce((newFields, fieldId) => {
    const parentType = fields[fieldId].parent.type.toUpperCase()
    if(parentType !== 'SECTION'){
      return {...newFields, [fieldId]: fields[fieldId]}
    }else{
      if(renderableFields.includes(fieldId)){
        return {...newFields, [fieldId]: fields[fieldId]}
      }else{
        console.warn(`Purged extraneous field with id: ${fieldId}, of type: ${fields[fieldId].type}, and labeled: "${fields[fieldId].label}"`)
        return newFields
      }
    }
  }, {})
  // Purge broken/null business rules
  fields = Object.keys(fields).reduce((newFields, fieldId) => {
    let field = {
      ...fields[fieldId],
      businessRules: fields[fieldId].businessRules.filter(br => br && BUSINESS_RULES[br.fn])
    }
    if(fields[fieldId].businessRules.length > field.businessRules.length){
      console.warn(`At least one corrupt business rule was identified and removed from field with label: ${field.label}`)
    }
    return {...newFields, [fieldId]: field}
  }, {})

  // Fix fields whose parents are repeaters, but are incorrectly classified.
  Object.keys(fields).forEach(fieldId => {
    let field = fields[fieldId]
    if(field){
      const fieldType = field.type.toUpperCase()
      if(fieldType === 'REPEATER'){
        field.template.forEach(subfieldId => {
          let subfield = fields[subfieldId]
          if(subfield){
            if(subfield.parent.type !== 'REPEATER'){
              const tempType = subfield.parent.type
              fields[subfieldId].parent.type = 'REPEATER'
              console.warn(`The following field had its parent type corrected from: ${tempType}, to: REPEATER`, fields[subfieldId])
            }
            if(subfield.parent.id !== field.fieldId){
              const tempId = subfield.parent.id
              fields[subfieldId].parent.id = field.fieldId
              console.warn(`The following field had its parent id corrected from: ${tempId}, to: ${field.fieldId}`, fields[subfieldId])
            }
          }
        })
      }
    }
  }, {})

  //Purge fields whose parents no longer exist
  fields = Object.keys(fields).reduce((newFields, fieldId) => {
    let field = fields[fieldId]
    const parentId = field.parent.id
    const parentType = field.parent.type.toUpperCase()
    let parentExists = false;
    switch (parentType) {
      case 'SECTION':
        parentExists = sections[parentId] ? true : false
        break;
      case 'STATIC':
        parentExists = true;
        break;
      case 'OPTION':
      case 'CHECKBOX':
      case 'REPEATER':
      default:
        parentExists = fields[parentId] ? true : false
    }
    if(parentExists){
      return {
        ...newFields,
        [fieldId]: field
      }
    }else{
      console.warn("The following field was purged from this wizard, because its parent no longer exists.", field)
      return newFields
    }
  }, {})

  return {steps, sections, fields}
}

const getMergeCodesFromString = (value='') => {
  if(typeof value !== 'string'){
    return {};
  }
  let codes = value.match(/{{(.*?)}}/g);
  return Array.isArray(codes) ? codes.reduce((obj, code) => ({...obj, [code]: true}), {}) : {}
}



// WEBPACK FOOTER //
// ./src/components/Wizard2/WizardClasses.js